import type { RedoManagedReturnsRpcClient } from "@redotech/redo-merchant-managed-returns-rpc/client";
import { createRedoManagedReturnsRpcClient } from "@redotech/redo-merchant-managed-returns-rpc/client";
import { ExponentialBackoffOptions } from "@redotech/util/retry";
import { Context, createContext, memo, ReactNode, useMemo } from "react";

export const RedoManagedReturnsRpcClientContext: Context<
  RedoManagedReturnsRpcClient | undefined
> = createContext<RedoManagedReturnsRpcClient | undefined>(undefined);
RedoManagedReturnsRpcClientContext.displayName =
  "RedoManagedReturnsRpcClientContext";

export const RedoManagedReturnsRpcClientProvider = memo(
  function RedoManagedReturnsRpcClientProvider({
    children,
    baseUrl,
    authToken,
  }: {
    children: ReactNode | ReactNode[];
    baseUrl: string;
    authToken?: string;
  }) {
    const redoManagedReturnsRpcClient = useMemo(() => {
      if (!authToken) {
        return undefined;
      }
      const retryOptions: ExponentialBackoffOptions = {
        maxAttempts: 2,
        baseDelay: Temporal.Duration.from({ milliseconds: 1000 }),
        maxDelay: Temporal.Duration.from({ seconds: 5 }),
        jitterFactor: 0.1,
        shouldRetry: (error) => {
          const statusCode = error?.status || error?.statusCode;
          return statusCode === 504 || statusCode === 502;
        },
      };
      return createRedoManagedReturnsRpcClient({
        baseURL: new URL(`${baseUrl}/managed-returns-rpc`),
        headers: { Authorization: authToken },
        retryOptions,
      });
    }, [baseUrl, authToken]);
    return (
      <RedoManagedReturnsRpcClientContext.Provider
        value={redoManagedReturnsRpcClient}
      >
        {children}
      </RedoManagedReturnsRpcClientContext.Provider>
    );
  },
);
